// Shared MoneyBot API client for the Kids/Teen chat (ChatScreen) and the Pro chat (ProChatScreen).
//
// The backend (/api/moneybot, see CLAUDE.md) currently accepts ONE `question` string of at most
// 800 chars and has no notion of a conversation. Without the earlier turns, follow-ups like
// "what if I did that for 5 years?" reach the model with no idea what "that" is. So we pack a
// compact recap of the last few turns into `question`, in priority order: the user's actual
// question > their financial context > recent history.
// We send ONLY the documented { question, mode } body, so a strictly-validating backend can't
// reject the request. When the backend gains a `history` field, send the turns there instead.

const API_URL = `${import.meta.env.VITE_API_URL}/api/moneybot`;
const MAX_QUESTION = 800;
const PACK_HISTORY_INTO_QUESTION = true;
const TIMEOUT_MS = 15000;
// Appended to every question so answers come back short and scannable on a phone, and so
// they fit whole in the history recap above. Lives here until it moves into the backend's
// system prompt (then drop it and reclaim the characters).
const ANSWER_STYLE = "(Answer in 3-5 short bullet points, one line each, about 80 words total. No intro or sign-off.)";

export type ApiMode = "kid" | "teen" | "adult";

/** A chat bubble. `kind: "chat"` marks real Q&A turns that count as conversation history
 *  (greetings, setup-wizard prompts and error notices are left out). */
export interface ChatMsg {
  role: "user" | "bot";
  text: string;
  kind?: "chat";
}

function clip(s: string, n: number): string {
  const t = s.replace(/[*#`]/g, "").replace(/\s+/g, " ").trim();
  return t.length <= n ? t : t.slice(0, Math.max(0, n - 1)).trimEnd() + "…";
}

/** Builds the 800-char `question` payload. Exported for testing. */
export function packQuestion(question: string, context: string | null, history: ChatMsg[]): string {
  const suffix = `\n\n${ANSWER_STYLE}`;
  const q = question.slice(0, MAX_QUESTION - suffix.length - 50);
  const label = "Current question: ";
  let budget = MAX_QUESTION - suffix.length - q.length;

  // Financial context comes before history — it's what makes Pro answers personal.
  let ctx = "";
  if (context) {
    const room = budget - label.length - 2;
    if (room > 50) ctx = context.length <= room ? context : clip(context, room);
  }

  const hasPrior = PACK_HISTORY_INTO_QUESTION && history.length > 0;
  const prefixLen = (ctx ? ctx.length + 2 : 0) + (hasPrior || ctx ? label.length : 0);
  budget -= prefixLen;

  // Newest turns first until we run out of room; bot answers clipped harder than user questions.
  const recap: string[] = [];
  if (hasPrior) {
    const header = "Earlier in this chat:\n";
    let room = budget - header.length - 2;
    for (let i = history.length - 1; i >= 0 && room > 40; i--) {
      const m = history[i];
      const line = m.role === "user" ? `Q: ${clip(m.text, 140)}` : `A: ${clip(m.text, 300)}`;
      const fitted = line.length + 1 <= room ? line : clip(line, room - 1);
      recap.unshift(fitted);
      room -= fitted.length + 1;
    }
    if (recap.length) recap.unshift(header.trimEnd());
  }

  const parts: string[] = [];
  if (ctx) parts.push(ctx);
  if (recap.length) parts.push(recap.join("\n"));
  const body = parts.length ? `${parts.join("\n\n")}\n\n${label}${q}` : q;
  return body.slice(0, MAX_QUESTION - suffix.length) + suffix;
}

export class MoneyBotError extends Error {
  constructor(public reason: "busy" | "timeout" | "failed", public detail = reason) {
    super(reason);
  }
}

// On the PREVIEW site only, say why the API call failed so David can diagnose from his phone.
// Production keeps the silent fallback (CLAUDE.md: users shouldn't see a "fallback" label).
const IS_PREVIEW = typeof location !== "undefined" && location.hostname === "moneyschool.stratagengroup.com";

export function previewDebug(err: unknown): string {
  if (!IS_PREVIEW) return "";
  const detail = err instanceof MoneyBotError ? err.detail : String(err);
  return `\n\n_(preview only — MoneyBot API failed: ${detail})_`;
}

export async function askMoneyBot(opts: {
  question: string;
  mode: ApiMode;
  context?: string | null;
  history?: ChatMsg[];
}): Promise<string> {
  const history = (opts.history ?? []).filter(m => m.kind === "chat");

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question: packQuestion(opts.question, opts.context ?? null, history),
        mode: opts.mode,
      }),
      signal: controller.signal,
    });
    if (res.status === 429) throw new MoneyBotError("busy");
    const data = await res.json().catch(() => null);
    if (!data?.ok || typeof data.answer !== "string") {
      const detail = `HTTP ${res.status}${data?.error ? ` — ${data.error}` : data ? "" : " (non-JSON response)"}`;
      console.warn("[MoneyBot] API error", detail);
      throw new MoneyBotError("failed", detail);
    }
    return data.answer;
  } catch (err: any) {
    if (err instanceof MoneyBotError) throw err;
    if (err?.name === "AbortError") throw new MoneyBotError("timeout");
    console.warn("[MoneyBot] request failed", err);
    throw new MoneyBotError("failed", `network/CORS error: ${err?.message ?? err} (URL ${API_URL})`);
  } finally {
    clearTimeout(timer);
  }
}

export const OFFLINE_MSG =
  "🤖 I couldn't reach my full brain just now, so I can't answer that one properly. Try again in a moment! 💬";
export const BUSY_MSG = "🤖 MoneyBot is really popular right now! Give it a few seconds and try again. 💬";
export const TIMEOUT_MSG = "🤖 That took a bit too long to answer. Try again in a moment! 💬";
